import { buildUrl, usKidsGolfService } from '@/libs/api/instances/us-kids-golf-instance';
import type { RequestOptions } from '@/libs/api/types';

/** Path (relative to `buildUrl`) for removing/leaving a co-guardian. */
export const playerGuardianMemberEndpoint = (playerId: string, guardianUid: string) =>
  `/guardians/me/players/${playerId}/guardians/${guardianUid}`;

/** Why a primary guardian removed a co-guardian, sent with the removal request. */
export type RemovalReason = 'no_longer_co_parenting' | 'safety_concern' | 'other';

/** Optional removal audit payload (primary guardian removing a co-guardian). */
export type RemovePlayerGuardianPayload = {
  reason: RemovalReason;
  /** Free-text note (max 500 chars), surfaced to the removed co-guardian. */
  detail?: string;
};

/**
 * Removes a co-guardian from a player's circle — an owner kicking a member, or the
 * caller leaving when `guardianUid` is their own uid. DELETE returns 204 (no content).
 * DELETE `/api/v1/guardians/me/players/{playerId}/guardians/{guardianUid}`.
 *
 * When a `payload` is supplied (primary guardian removing a member), it is sent as the
 * request body `{ reason, detail }` for the removal audit + co-guardian notification.
 */
export async function removePlayerGuardian(
  playerId: string,
  guardianUid: string,
  payload?: RemovePlayerGuardianPayload,
  options?: RequestOptions,
): Promise<void> {
  // DELETE carries a body here (the removal audit), which RequestOptions omits by default.
  const config = payload ? ({ ...options, data: payload } as RequestOptions) : options;
  await usKidsGolfService.delete<void>(
    buildUrl(playerGuardianMemberEndpoint(playerId, guardianUid)),
    config,
  );
}
